import { DATE_RANGE_TEXT, plausibleDate } from './dates.js';
/*
 * Formulare (0.30): Modell, gemeinsamer Auswerter und Kartenzuordnung.
 *
 * Der Auswerter ist rein und ohne zod, damit Browser (Bedienung, Vorschau) und Server (verbindliche
 * Annahme) dieselbe Logik verwenden (T04). Reihenfolge der Auswertung ist die Dokumentreihenfolge:
 * Bedingungen und Formeln dürfen nur auf frühere Fragen verweisen (R03) – damit gibt es keine Zyklen.
 */
export const FORM_SCHEMA_VERSION = 1;
export const FORM_LIMITS = { questions: 100, pages: 15, rules: 100, conditionsPerRule: 10, rows: 20, options: 50, routes: 30 } as const;

/* ---------- Modell ---------- */

export const FORM_QUESTION_TYPES = [
  'text',
  'longtext',
  'email',
  'phone',
  'url',
  'number',
  'currency',
  'choice',
  'multichoice',
  'yesno',
  'confirm',
  'date',
  'datetime',
  'file',
  'rating',
  'calc',
  'info',
] as const;
export type FormQuestionType = (typeof FORM_QUESTION_TYPES)[number];

export const FORM_QUESTION_LABELS: Record<FormQuestionType, string> = {
  text: 'Kurzer Text',
  longtext: 'Langer Text',
  email: 'E-Mail-Adresse',
  phone: 'Telefon',
  url: 'Internetadresse',
  number: 'Zahl',
  currency: 'Betrag (€)',
  choice: 'Einfachauswahl',
  multichoice: 'Mehrfachauswahl',
  yesno: 'Ja/Nein',
  confirm: 'Bestätigung',
  date: 'Datum',
  datetime: 'Datum mit Uhrzeit',
  file: 'Dateien / Bilder',
  rating: 'Bewertung 1–5',
  calc: 'Berechneter Wert',
  info: 'Überschrift / Hinweis',
};

export const FORM_OPS = ['eq', 'neq', 'in', 'contains', 'containsAny', 'containsAll', 'startsWith', 'gt', 'gte', 'lt', 'lte', 'between', 'answered', 'unanswered'] as const;
export type FormOp = (typeof FORM_OPS)[number];

/** Bedingung: Feld → Vergleich → Wert */
export interface FormAtom {
  q: string;
  op: FormOp;
  value?: string | number | boolean | string[] | [number | string, number | string] | null;
}
export interface FormGroup {
  mode: 'all' | 'any';
  items: (FormAtom | FormGroup)[];
}
export type FormCondition = FormGroup;

export interface FormOption {
  id: string;
  label: string;
  /** Option nur anbieten, wenn … (Auswahl einschränken, R02) */
  visibleIf?: FormCondition | null;
}

/** Formel als Baum (R06): keine Ausführung von Text */
export type FormExpr =
  | { k: 'num'; v: number }
  | { k: 'ref'; q: string }
  | { k: 'sum'; q: string }
  | { k: 'op'; op: '+' | '-' | '*' | '/'; a: FormExpr; b: FormExpr };

export interface FormQuestion {
  kind: 'question';
  id: string;
  type: FormQuestionType;
  label: string;
  help?: string;
  placeholder?: string;
  required?: boolean;
  visibleIf?: FormCondition | null;
  /** Nur Pflicht, wenn … (sonst gilt `required`) */
  requiredIf?: FormCondition | null;
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  /** Nachkommastellen (Zahl; 0 = ganze Zahl). Betrag immer 2 */
  decimals?: number;
  unit?: string;
  options?: FormOption[];
  /** Darstellung der Einfachauswahl */
  display?: 'auto' | 'radio' | 'select';
  minSelect?: number;
  maxSelect?: number;
  /** Datum/Zeitpunkt darf nicht vor dieser Frage liegen (Feldvergleich, R05) */
  notBefore?: string;
  maxFiles?: number;
  maxSizeMb?: number;
  /** Erlaubte Dateiarten */
  accept?: ('image' | 'pdf' | 'text')[];
  formula?: FormExpr;
  /** Beschriftung der Skalenenden (Bewertung) */
  lowLabel?: string;
  highLabel?: string;
}

export interface FormSection {
  kind: 'section';
  id: string;
  title: string;
  help?: string;
  visibleIf?: FormCondition | null;
  /** Wiederholbarer Abschnitt (R07) */
  repeat?: { min: number; max: number; itemLabel: string } | null;
  items: FormQuestion[];
}

export type FormItem = FormQuestion | FormSection;

export interface FormPage {
  id: string;
  title: string;
  visibleIf?: FormCondition | null;
  items: FormItem[];
}

/** Zuordnung zur Karte (nur intern, nie öffentlich – A02) */
export interface FormMapping {
  columnId: string | null;
  /** Zielspalte wurde bewusst bestätigt (U01) */
  columnConfirmed: boolean;
  placement: 'top' | 'bottom';
  /** Textvorlage mit Platzhaltern {{q:<id>}} */
  title: string;
  description: { mode: 'summary' | 'none'; questionIds?: string[] | null };
  /**
   * Antwort → Boardfeld. Bei Auswahlfragen verbindet `options` Formularoptions-ID → Feldoptions-ID (F03);
   * Bezeichnungen dienen nur der Anzeige. Fehlt `options`, gilt die eindeutige Namensgleichheit nur als Einrichthilfe.
   */
  fields: { fieldId: string; q: string; options?: Record<string, string> | null }[];
  start?: { q: string } | null;
  due?: { q: string } | { days: number } | null;
  labelIds: string[];
  priority: number;
  assigneeIds: string[];
  coverQ?: string | null;
  cardTemplateId?: string | null;
  /** Kontaktangabe der Einsendung (ungeprüft, nie als Identität behandelt) */
  contactQ?: string | null;
}

export interface FormRoute {
  id: string;
  name: string;
  when: FormCondition;
  columnId?: string | null;
  labels?: { mode: 'add' | 'replace'; ids: string[] } | null;
  assignees?: { mode: 'add' | 'replace'; ids: string[] } | null;
  priority?: number | null;
  dueDays?: number | null;
}

export interface FormTestCase {
  id: string;
  name: string;
  answers: FormAnswers;
}

export interface FormDefinition {
  schemaVersion: 1;
  title: string;
  intro: string;
  pages: FormPage[];
  mapping: FormMapping;
  routes: FormRoute[];
  confirmation: { text: string; expectation?: string };
  tests: FormTestCase[];
}

/** Öffentliche, bereinigte Darstellung (ohne Zuordnung, Regeln und Tests) */
export type FormPublicDefinition = Pick<FormDefinition, 'schemaVersion' | 'title' | 'intro' | 'pages'> & { confirmation: { text: string } };

export type FormRowValue = { _id: string } & Record<string, FormValue>;
export type FormValue = string | number | boolean | string[] | FormRowValue[] | null;
export type FormAnswers = Record<string, FormValue>;

/** Hochgeladene Datei aus Sicht des Auswerters */
export interface FormUploadInfo {
  id: string;
  key: string;
  name: string;
  mime: string;
  size: number;
  status: 'ready' | 'quarantine' | 'failed';
}

/* ---------- Hilfen für die Struktur ---------- */

export interface FlatQuestion {
  q: FormQuestion;
  page: FormPage;
  section: FormSection | null;
  /** Position in Dokumentreihenfolge */
  order: number;
}

/** Alle Fragen in Dokumentreihenfolge (Abschnitte aufgelöst) */
export function flattenQuestions(def: Pick<FormDefinition, 'pages'>): FlatQuestion[] {
  const out: FlatQuestion[] = [];
  for (const page of def.pages)
    for (const item of page.items) {
      if (item.kind === 'section') for (const q of item.items) out.push({ q, page, section: item, order: out.length });
      else out.push({ q: item, page, section: null, order: out.length });
    }
  return out;
}

export function findSection(def: Pick<FormDefinition, 'pages'>, id: string): FormSection | null {
  for (const p of def.pages) for (const it of p.items) if (it.kind === 'section' && it.id === id) return it;
  return null;
}

const ANSWERLESS = new Set<FormQuestionType>(['info', 'calc']);
export const isAnswerable = (q: FormQuestion) => !ANSWERLESS.has(q.type);

/** Schlüssel einer Antwort: Frage oder Frage in einer Zeile */
export const rowKey = (sectionId: string, rowId: string, qid: string) => `${sectionId}[${rowId}].${qid}`;

/* ---------- Dezimalrechnung (R06) ---------- */

const SCALE = 10n ** 12n;
function toFixed(n: number): bigint {
  const s = n.toFixed(12);
  const neg = s.startsWith('-');
  const [i, f = ''] = s.replace('-', '').split('.');
  const v = BigInt(i!) * SCALE + BigInt((f + '000000000000').slice(0, 12));
  return neg ? -v : v;
}
function divRound(a: bigint, b: bigint): bigint {
  const q = a / b;
  const r = a % b;
  // kaufmännisch: halb weg von null
  if (2n * (r < 0n ? -r : r) >= (b < 0n ? -b : b)) return q + ((a < 0n) !== (b < 0n) ? -1n : 1n);
  return q;
}
function fromFixed(v: bigint): number {
  const neg = v < 0n;
  const a = neg ? -v : v;
  const s = `${a / SCALE}.${(a % SCALE).toString().padStart(12, '0')}`;
  return Number(s) * (neg ? -1 : 1);
}
/** Kaufmännisch auf n Stellen runden (0,005 → 0,01) */
export function roundDecimal(x: number, decimals: number): number {
  const v = toFixed(x);
  const unit = 10n ** BigInt(12 - Math.max(0, Math.min(12, decimals)));
  return fromFixed(divRound(v, unit) * unit);
}

/* ---------- Werte lesen ---------- */

const isEmpty = (v: FormValue | undefined): boolean =>
  v === undefined || v === null || (typeof v === 'string' && v.trim() === '') || (Array.isArray(v) && v.length === 0);

function asNumber(v: FormValue | undefined): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  return null;
}

/** Vergleichswert für Datum: „JJJJ-MM-TT“ oder Zeitpunkt in ms */
function dateKey(v: unknown): number | null {
  if (typeof v !== 'string' || !v) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return Date.parse(`${v}T00:00:00Z`);
  const t = Date.parse(v);
  return Number.isNaN(t) ? null : t;
}

/* ---------- Bedingungen ---------- */

type Lookup = (q: string) => { value: FormValue | undefined; type: FormQuestionType | 'section' } | null;

function evalAtom(a: FormAtom, look: Lookup): boolean {
  const hit = look(a.q);
  const v = hit?.value;
  const empty = isEmpty(v);
  if (a.op === 'unanswered') return empty;
  if (a.op === 'answered') return !empty;
  if (empty || !hit) return false; // fehlende Antwort: alle Vergleiche falsch, auch „ist nicht“ (R03)
  const t = hit.type;
  const want = a.value;
  if (t === 'section') {
    const n = Array.isArray(v) ? v.length : 0;
    return cmpNumber(a.op, n, want);
  }
  if (t === 'multichoice') {
    const set = new Set(Array.isArray(v) ? (v as string[]) : []);
    const list = Array.isArray(want) ? (want as string[]) : typeof want === 'string' ? [want] : [];
    if (a.op === 'contains' || a.op === 'containsAny') return list.some((x) => set.has(x));
    if (a.op === 'containsAll') return list.every((x) => set.has(x));
    return false;
  }
  if (t === 'choice') {
    if (a.op === 'eq') return v === want;
    if (a.op === 'neq') return v !== want;
    if (a.op === 'in') return Array.isArray(want) && (want as string[]).includes(v as string);
    return false;
  }
  if (t === 'yesno' || t === 'confirm') {
    if (a.op === 'eq') return v === want;
    if (a.op === 'neq') return v !== want;
    return false;
  }
  if (t === 'text' || t === 'longtext' || t === 'email' || t === 'phone' || t === 'url') {
    const s = String(v).toLocaleLowerCase('de-DE').trim();
    const w = String(want ?? '').toLocaleLowerCase('de-DE').trim();
    if (a.op === 'eq') return s === w;
    if (a.op === 'neq') return s !== w;
    if (a.op === 'contains') return s.includes(w);
    if (a.op === 'startsWith') return s.startsWith(w);
    return false;
  }
  if (t === 'date' || t === 'datetime') {
    const x = dateKey(v);
    if (x === null) return false;
    const day = t === 'date';
    const k = (w: unknown) => dateKey(w);
    if (a.op === 'between') {
      const [lo, hi] = Array.isArray(want) ? want : [];
      const l = k(lo);
      const h = k(hi);
      return l !== null && h !== null && x >= l && x <= (day ? h : h);
    }
    const w = k(want);
    if (w === null) return false;
    if (a.op === 'eq') return day ? x === w : new Date(x).toISOString().slice(0, 10) === new Date(w).toISOString().slice(0, 10);
    return cmpNumber(a.op, x, w);
  }
  // Zahl, Betrag, Bewertung, Berechnung
  const n = asNumber(v);
  if (n === null) return false;
  return cmpNumber(a.op, n, want);
}

function cmpNumber(op: FormOp, n: number, want: unknown): boolean {
  if (op === 'between') {
    const [lo, hi] = Array.isArray(want) ? want.map(Number) : [];
    return lo !== undefined && hi !== undefined && Number.isFinite(lo) && Number.isFinite(hi) && n >= lo && n <= hi;
  }
  const w = typeof want === 'number' ? want : Number(want);
  if (!Number.isFinite(w)) return false;
  switch (op) {
    case 'eq':
      return n === w;
    case 'neq':
      return n !== w;
    case 'gt':
      return n > w;
    case 'gte':
      return n >= w;
    case 'lt':
      return n < w;
    case 'lte':
      return n <= w;
    default:
      return false;
  }
}

export function evalCondition(c: FormCondition | null | undefined, look: Lookup): boolean {
  if (!c || !c.items.length) return true;
  const res = c.items.map((it) => ('q' in it ? evalAtom(it, look) : evalCondition(it, look)));
  return c.mode === 'all' ? res.every(Boolean) : res.some(Boolean);
}

export function conditionRefs(c: FormCondition | null | undefined): string[] {
  if (!c) return [];
  return c.items.flatMap((it) => ('q' in it ? [it.q] : conditionRefs(it)));
}

/* ---------- Formeln ---------- */

export function exprRefs(e: FormExpr | undefined): { refs: string[]; sums: string[] } {
  const refs: string[] = [];
  const sums: string[] = [];
  const walk = (x: FormExpr) => {
    if (x.k === 'ref') refs.push(x.q);
    else if (x.k === 'sum') sums.push(x.q);
    else if (x.k === 'op') {
      walk(x.a);
      walk(x.b);
    }
  };
  if (e) walk(e);
  return { refs, sums };
}

type CalcResult = { ok: true; value: bigint } | { ok: false; error: 'missing' | 'div0' };

function evalExpr(e: FormExpr, num: (q: string) => number | null, sum: (q: string) => number | null): CalcResult {
  switch (e.k) {
    case 'num':
      return { ok: true, value: toFixed(e.v) };
    case 'ref': {
      const n = num(e.q);
      return n === null ? { ok: false, error: 'missing' } : { ok: true, value: toFixed(n) };
    }
    case 'sum': {
      const n = sum(e.q);
      return n === null ? { ok: false, error: 'missing' } : { ok: true, value: toFixed(n) };
    }
    case 'op': {
      const a = evalExpr(e.a, num, sum);
      if (!a.ok) return a;
      const b = evalExpr(e.b, num, sum);
      if (!b.ok) return b;
      if (e.op === '+') return { ok: true, value: a.value + b.value };
      if (e.op === '-') return { ok: true, value: a.value - b.value };
      if (e.op === '*') return { ok: true, value: divRound(a.value * b.value, SCALE) };
      if (b.value === 0n) return { ok: false, error: 'div0' };
      return { ok: true, value: divRound(a.value * SCALE, b.value) };
    }
  }
}

/** 0.82: Formel ausrechnen (für berechnete Felder) – exakt dezimal, null bei fehlendem Wert oder Division durch 0 */
export function evalFormulaNumber(e: FormExpr, num: (q: string) => number | null): number | null {
  const r = evalExpr(e, num, () => null);
  return r.ok ? fromFixed(r.value) : null;
}

/**
 * 0.85 (N02): eine Rechnung exakt dezimal (wie Formeln) – für „Zahl rechnen“ in Automationen.
 * null bei Division durch 0. Danach mit `roundDecimal` auf die Zielgenauigkeit runden.
 */
export function decimalCalc(a: number, op: '+' | '-' | '*' | '/', b: number): number | null {
  return evalFormulaNumber({ k: 'op', op, a: { k: 'num', v: a }, b: { k: 'num', v: b } }, () => null);
}

/** Formel als Text mit Fragenbeschriftungen: [Menge] * [Einzelpreis], SUMME([Gesamt]) */
export function formatExpr(e: FormExpr | undefined, label: (q: string) => string): string {
  if (!e) return '';
  const prec = (x: FormExpr) => (x.k === 'op' ? (x.op === '+' || x.op === '-' ? 1 : 2) : 3);
  const f = (x: FormExpr): string => {
    if (x.k === 'num') return String(x.v).replace('.', ',');
    if (x.k === 'ref') return `[${label(x.q)}]`;
    if (x.k === 'sum') return `SUMME([${label(x.q)}])`;
    const p = prec(x);
    const l = prec(x.a) < p ? `(${f(x.a)})` : f(x.a);
    const r = prec(x.b) < p || (prec(x.b) === p && (x.op === '-' || x.op === '/')) ? `(${f(x.b)})` : f(x.b);
    return `${l} ${x.op === '*' ? '×' : x.op === '/' ? '÷' : x.op} ${r}`;
  };
  return f(e);
}

export class FormulaError extends Error {}

/** Liest eine Formel; Verweise über Beschriftungen in eckigen Klammern (resolve → ID oder null) */
export function parseFormula(src: string, resolve: (label: string) => string | null): FormExpr {
  let i = 0;
  const s = src;
  const ws = () => {
    while (i < s.length && /\s/.test(s[i]!)) i++;
  };
  const ref = (): string => {
    if (s[i] !== '[') throw new FormulaError('Verweis in eckigen Klammern erwartet, z. B. [Menge].');
    const end = s.indexOf(']', i);
    if (end < 0) throw new FormulaError('Schließende Klammer ] fehlt.');
    const label = s.slice(i + 1, end).trim();
    i = end + 1;
    const id = resolve(label);
    if (!id) throw new FormulaError(`Keine Frage „${label}“ gefunden.`);
    return id;
  };
  const primary = (): FormExpr => {
    ws();
    if (s[i] === '(') {
      i++;
      const e = expr();
      ws();
      if (s[i] !== ')') throw new FormulaError('Schließende Klammer ) fehlt.');
      i++;
      return e;
    }
    if (s[i] === '[') return { k: 'ref', q: ref() };
    const m = /^(SUMME|SUM)\s*\(\s*/i.exec(s.slice(i));
    if (m) {
      i += m[0].length;
      const q = ref();
      ws();
      if (s[i] !== ')') throw new FormulaError('Schließende Klammer ) nach SUMME fehlt.');
      i++;
      return { k: 'sum', q };
    }
    const n = /^-?\d+(?:[.,]\d+)?/.exec(s.slice(i));
    if (n) {
      i += n[0].length;
      return { k: 'num', v: Number(n[0].replace(',', '.')) };
    }
    throw new FormulaError(i >= s.length ? 'Die Formel ist unvollständig.' : `Unerwartetes Zeichen „${s[i]}“.`);
  };
  const term = (): FormExpr => {
    let a = primary();
    for (;;) {
      ws();
      const c = s[i];
      if (c === '*' || c === '×' || c === '/' || c === '÷' || c === ':') {
        i++;
        a = { k: 'op', op: c === '*' || c === '×' ? '*' : '/', a, b: primary() };
      } else return a;
    }
  };
  const expr = (): FormExpr => {
    let a = term();
    for (;;) {
      ws();
      const c = s[i];
      if (c === '+' || c === '-' || c === '−') {
        i++;
        a = { k: 'op', op: c === '+' ? '+' : '-', a, b: term() };
      } else return a;
    }
  };
  const e = expr();
  ws();
  if (i < s.length) throw new FormulaError(`Unerwartetes Zeichen „${s[i]}“.`);
  return e;
}

/* ---------- Auswertung ---------- */

export interface FormEvaluation {
  /** Sichtbare Seiten in Reihenfolge */
  pages: string[];
  /** Sichtbare Fragen/Abschnitte (Schlüssel) */
  visible: Set<string>;
  required: Set<string>;
  /** Sichtbare Optionen je Schlüssel (nur Auswahlfragen) */
  options: Record<string, string[]>;
  /** Aktive Antworten: verborgene entfernt, berechnete Werte eingesetzt */
  active: FormAnswers;
  /** Berechnete Werte bzw. Grund, warum (noch) keiner vorliegt */
  calc: Record<string, { value: number | null; error?: 'missing' | 'div0' }>;
  /** Entfernte Antworten (verborgen oder unzulässige Option) */
  removed: string[];
  /** Für „Warum sichtbar?“: entscheidende Bedingung je Schlüssel */
  because: Record<string, FormCondition>;
}

function rowsOf(v: FormValue | undefined): FormRowValue[] {
  return Array.isArray(v) && v.every((r) => r && typeof r === 'object' && !Array.isArray(r)) ? (v as FormRowValue[]) : [];
}

/**
 * Sichtbarkeit, Pflicht, Optionen, Berechnungen und aktive Antworten (R03, R06, R07).
 * Reihenfolge = Dokumentreihenfolge; eine verborgene Frage hat für spätere Regeln keinen Wert.
 */
export function evaluateForm(def: Pick<FormDefinition, 'pages'>, answers: FormAnswers): FormEvaluation {
  const ev: FormEvaluation = { pages: [], visible: new Set(), required: new Set(), options: {}, active: {}, calc: {}, removed: [], because: {} };
  const types = new Map<string, FormQuestionType | 'section'>();
  for (const p of def.pages)
    for (const it of p.items) {
      types.set(it.id, it.kind === 'section' ? 'section' : it.type);
      if (it.kind === 'section') for (const q of it.items) types.set(q.id, q.type);
    }
  const global: Lookup = (q) => {
    const t = types.get(q);
    return t ? { value: ev.active[q], type: t } : null;
  };

  const handleQuestion = (q: FormQuestion, key: string, raw: FormValue | undefined, parentVisible: boolean, look: Lookup, store: (v: FormValue) => void) => {
    const vis = parentVisible && evalCondition(q.visibleIf, look);
    if (vis && q.visibleIf?.items.length) ev.because[key] = q.visibleIf;
    if (!vis) {
      if (!isEmpty(raw) && isAnswerable(q)) ev.removed.push(key);
      return;
    }
    ev.visible.add(key);
    if (q.type === 'info') return;
    if (q.type === 'calc') {
      const res = q.formula
        ? evalExpr(
            q.formula,
            (r) => asNumber(look(r)?.value),
            (r) => sumOf(r),
          )
        : ({ ok: false, error: 'missing' } as CalcResult);
      if (res.ok) {
        const v = roundDecimal(fromFixed(res.value), q.decimals ?? 2);
        ev.calc[key] = { value: v };
        store(v);
      } else ev.calc[key] = { value: null, error: res.error };
      return;
    }
    const req = !!q.required || (!!q.requiredIf && evalCondition(q.requiredIf, look));
    if (req) ev.required.add(key);
    let v = raw;
    if (q.type === 'choice' || q.type === 'multichoice') {
      const opts = (q.options ?? []).filter((o) => evalCondition(o.visibleIf, look)).map((o) => o.id);
      ev.options[key] = opts;
      if (q.type === 'choice' && typeof v === 'string' && !opts.includes(v)) {
        ev.removed.push(key);
        v = undefined;
      }
      if (q.type === 'multichoice' && Array.isArray(v)) {
        const keep = (v as string[]).filter((x) => opts.includes(x));
        if (keep.length !== v.length) ev.removed.push(key);
        v = keep;
      }
    }
    if (!isEmpty(v)) store(v!);
  };

  // Summen über Zeilen eines früheren Abschnitts
  const sumOf = (qid: string): number | null => {
    for (const [sid, rows] of Object.entries(ev.active)) {
      if (types.get(sid) !== 'section' || !Array.isArray(rows)) continue;
      const sec = findSection(def, sid);
      if (!sec?.items.some((x) => x.id === qid)) continue;
      let total = 0n;
      for (const r of rows as FormRowValue[]) {
        const n = asNumber(r[qid]);
        if (n === null) return null; // fehlender Wert in einer Zeile: noch nicht berechenbar
        total += toFixed(n);
      }
      return fromFixed(total);
    }
    // Abschnitt verborgen oder ohne Zeilen
    const sec = def.pages.flatMap((p) => p.items).find((it) => it.kind === 'section' && it.items.some((x) => x.id === qid)) as FormSection | undefined;
    if (sec && ev.visible.has(sec.id)) return 0;
    return null;
  };

  for (const page of def.pages) {
    const pVis = evalCondition(page.visibleIf, global);
    if (pVis) {
      ev.pages.push(page.id);
      if (page.visibleIf?.items.length) ev.because[page.id] = page.visibleIf;
    }
    for (const item of page.items) {
      if (item.kind === 'question') {
        handleQuestion(item, item.id, answers[item.id], pVis, global, (v) => (ev.active[item.id] = v));
        continue;
      }
      const sVis = pVis && evalCondition(item.visibleIf, global);
      if (sVis && item.visibleIf?.items.length) ev.because[item.id] = item.visibleIf;
      if (!item.repeat) {
        if (sVis) ev.visible.add(item.id);
        for (const q of item.items) handleQuestion(q, q.id, answers[q.id], sVis, global, (v) => (ev.active[q.id] = v));
        continue;
      }
      const rows = rowsOf(answers[item.id]).slice(0, Math.min(item.repeat.max, FORM_LIMITS.rows));
      if (!sVis) {
        if (rows.some((r) => item.items.some((q) => !isEmpty(r[q.id])))) ev.removed.push(item.id);
        continue;
      }
      ev.visible.add(item.id);
      const outRows: FormRowValue[] = [];
      for (const r of rows) {
        const rowId = typeof r._id === 'string' && r._id ? r._id : '';
        if (!rowId) continue;
        const out: FormRowValue = { _id: rowId };
        // Innerhalb einer Zeile: frühere Felder dieser Zeile, sonst globale Fragen (R07)
        const look: Lookup = (q) => {
          if (item.items.some((x) => x.id === q)) return { value: out[q], type: types.get(q)! };
          return global(q);
        };
        for (const q of item.items) handleQuestion(q, rowKey(item.id, rowId, q.id), r[q.id], true, look, (v) => (out[q.id] = v));
        outRows.push(out);
      }
      ev.active[item.id] = outRows;
    }
  }
  return ev;
}

/* ---------- Validierung (R05) ---------- */

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE = /^\+?[\d\s/().-]{5,30}$/;
const URL_RE = /^https?:\/\/[^\s]+\.[^\s]+$/i;
export const FORM_ACCEPT_MIME: Record<'image' | 'pdf' | 'text', string[]> = {
  image: ['image/jpeg', 'image/png', 'image/webp'],
  pdf: ['application/pdf'],
  text: ['text/plain'],
};

export interface FormIssue {
  key: string;
  message: string;
}

/**
 * Prüft sichtbare Fragen. `pageId` begrenzt auf eine Seite („Weiter“), sonst alle sichtbaren Seiten (Absenden).
 * `uploads` sind die Dateien der Sitzung (nur fertig geprüfte erfüllen die Pflicht).
 */
export function validateForm(
  def: Pick<FormDefinition, 'pages'>,
  ev: FormEvaluation,
  opts: { pageId?: string; uploads?: FormUploadInfo[] } = {},
): FormIssue[] {
  const issues: FormIssue[] = [];
  const uploads = new Map((opts.uploads ?? []).map((u) => [u.id, u]));
  const check = (q: FormQuestion, key: string, v: FormValue | undefined, row: FormRowValue | null) => {
    if (!ev.visible.has(key) || !isAnswerable(q)) return;
    const req = ev.required.has(key);
    const add = (message: string) => issues.push({ key, message });
    if (q.type === 'confirm') {
      if (req && v !== true) add('Bitte bestätigen.');
      return;
    }
    if (q.type === 'file') {
      const ids = Array.isArray(v) ? (v as string[]) : [];
      const ready = ids.filter((id) => uploads.get(id)?.status === 'ready');
      if (req && !ready.length) return add(ids.length ? 'Die Datei wird noch geprüft.' : 'Bitte eine Datei hochladen.');
      if (ids.length > (q.maxFiles ?? 5)) add(`Höchstens ${q.maxFiles ?? 5} Dateien.`);
      if (ids.some((id) => uploads.get(id)?.status === 'failed')) add('Eine Datei wurde abgelehnt. Bitte entferne sie.');
      return;
    }
    if (isEmpty(v)) {
      if (req) add(q.type === 'choice' || q.type === 'multichoice' || q.type === 'yesno' || q.type === 'rating' ? 'Bitte auswählen.' : 'Bitte ausfüllen.');
      return;
    }
    switch (q.type) {
      case 'text':
      case 'longtext': {
        const s = String(v);
        const max = q.maxLength ?? (q.type === 'text' ? 300 : 5000);
        if (s.length > max) add(`Höchstens ${max} Zeichen.`);
        if (q.minLength && s.trim().length < q.minLength) add(`Mindestens ${q.minLength} Zeichen.`);
        break;
      }
      case 'email':
        if (!EMAIL.test(String(v).trim())) add('Bitte eine gültige E-Mail-Adresse angeben.');
        break;
      case 'phone':
        if (!PHONE.test(String(v).trim())) add('Bitte eine gültige Telefonnummer angeben.');
        break;
      case 'url':
        if (!URL_RE.test(String(v).trim())) add('Bitte eine Adresse mit http:// oder https:// angeben.');
        break;
      case 'number':
      case 'currency': {
        const n = asNumber(v);
        if (n === null) {
          add('Bitte eine Zahl angeben.');
          break;
        }
        const dec = q.type === 'currency' ? 2 : (q.decimals ?? 0);
        if (roundDecimal(n, dec) !== n) add(dec === 0 ? 'Bitte eine ganze Zahl angeben.' : `Höchstens ${dec} Nachkommastellen.`);
        if (q.min !== undefined && n < q.min) add(`Mindestens ${fmtNum(q.min)}${q.unit ? ' ' + q.unit : ''}.`);
        if (q.max !== undefined && n > q.max) add(`Höchstens ${fmtNum(q.max)}${q.unit ? ' ' + q.unit : ''}.`);
        break;
      }
      case 'rating': {
        const n = asNumber(v);
        if (n === null || !Number.isInteger(n) || n < 1 || n > 5) add('Bitte eine Bewertung von 1 bis 5 wählen.');
        break;
      }
      case 'choice':
        if (typeof v !== 'string' || !(ev.options[key] ?? []).includes(v)) add('Bitte eine der angebotenen Antworten wählen.');
        break;
      case 'multichoice': {
        const n = Array.isArray(v) ? v.length : 0;
        if (q.minSelect && n < q.minSelect) add(`Bitte mindestens ${q.minSelect} auswählen.`);
        if (q.maxSelect && n > q.maxSelect) add(`Bitte höchstens ${q.maxSelect} auswählen.`);
        break;
      }
      case 'yesno':
        if (typeof v !== 'boolean') add('Bitte Ja oder Nein wählen.');
        break;
      case 'date':
      case 'datetime': {
        const ok = q.type === 'date' ? typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) && dateKey(v) !== null : dateKey(v) !== null;
        if (!ok) {
          add('Bitte ein gültiges Datum angeben.');
          break;
        }
        if (typeof v === 'string' && !plausibleDate(v)) {
          add(DATE_RANGE_TEXT);
          break;
        }
        if (q.notBefore) {
          const other = row && q.notBefore in row ? row[q.notBefore] : ev.active[q.notBefore];
          const a = dateKey(v);
          const b = dateKey(other);
          if (a !== null && b !== null && a < b) add('Das Datum darf nicht vor dem Beginn liegen.');
        }
        break;
      }
    }
  };
  for (const page of def.pages) {
    if (!ev.pages.includes(page.id)) continue;
    if (opts.pageId && page.id !== opts.pageId) continue;
    for (const item of page.items) {
      if (item.kind === 'question') {
        check(item, item.id, ev.active[item.id], null);
        continue;
      }
      if (!item.repeat) {
        for (const q of item.items) check(q, q.id, ev.active[q.id], null);
        continue;
      }
      if (!ev.visible.has(item.id)) continue;
      const rows = rowsOf(ev.active[item.id]);
      if (rows.length < item.repeat.min) issues.push({ key: item.id, message: `Bitte mindestens ${item.repeat.min} ${item.repeat.min === 1 ? 'Eintrag' : 'Einträge'} angeben.` });
      if (rows.length > item.repeat.max) issues.push({ key: item.id, message: `Höchstens ${item.repeat.max} Einträge.` });
      for (const r of rows) for (const q of item.items) check(q, rowKey(item.id, r._id, q.id), r[q.id], r);
    }
  }
  return issues;
}

const fmtNum = (n: number) => n.toLocaleString('de-DE', { maximumFractionDigits: 10 });

/* ---------- Normalisieren (Server: unbekannte IDs zurückweisen) ---------- */

export class FormInputError extends Error {}

/** Typen prüfen und angleichen; wirft bei unbekannten Fragen oder Zeilen-IDs */
export function normalizeAnswers(def: Pick<FormDefinition, 'pages'>, raw: unknown): FormAnswers {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) throw new FormInputError('Antworten fehlen.');
  const flat = flattenQuestions(def);
  const byId = new Map(flat.map((f) => [f.q.id, f]));
  const sections = new Map<string, FormSection>();
  for (const p of def.pages) for (const it of p.items) if (it.kind === 'section' && it.repeat) sections.set(it.id, it);
  const out: FormAnswers = {};
  const one = (q: FormQuestion, v: unknown): FormValue => {
    if (v === null || v === undefined) return null;
    switch (q.type) {
      case 'text':
      case 'longtext':
      case 'email':
      case 'phone':
      case 'url':
      case 'date':
      case 'datetime':
      case 'choice':
        if (typeof v !== 'string') throw new FormInputError(`Ungültiger Wert für „${q.label}“.`);
        return v.slice(0, 20_000);
      case 'number':
      case 'currency':
      case 'rating':
        if (typeof v === 'number' && Number.isFinite(v)) return v;
        throw new FormInputError(`Ungültige Zahl für „${q.label}“.`);
      case 'yesno':
      case 'confirm':
        if (typeof v !== 'boolean') throw new FormInputError(`Ungültiger Wert für „${q.label}“.`);
        return v;
      case 'multichoice':
      case 'file':
        if (!Array.isArray(v) || v.some((x) => typeof x !== 'string') || v.length > 100) throw new FormInputError(`Ungültiger Wert für „${q.label}“.`);
        return [...new Set(v as string[])];
      default:
        return null; // Berechnungen und Hinweise werden neu ermittelt
    }
  };
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    const sec = sections.get(k);
    if (sec) {
      if (!Array.isArray(v) || v.length > Math.min(sec.repeat!.max, FORM_LIMITS.rows)) throw new FormInputError(`Ungültige Einträge in „${sec.title}“.`);
      const seen = new Set<string>();
      out[k] = v.map((r) => {
        if (!r || typeof r !== 'object' || typeof (r as { _id?: unknown })._id !== 'string') throw new FormInputError('Ungültige Zeile.');
        const row: FormRowValue = { _id: (r as { _id: string })._id.slice(0, 64) };
        if (seen.has(row._id)) throw new FormInputError('Doppelte Zeile.');
        seen.add(row._id);
        for (const [rk, rv] of Object.entries(r as Record<string, unknown>)) {
          if (rk === '_id') continue;
          const q = sec.items.find((x) => x.id === rk);
          if (!q) throw new FormInputError('Unbekannte Frage in einer Zeile.');
          row[rk] = one(q, rv);
        }
        return row;
      });
      continue;
    }
    const f = byId.get(k);
    if (!f || (f.section?.repeat ?? null)) throw new FormInputError('Unbekannte Frage.');
    out[k] = one(f.q, v);
  }
  return out;
}

/* ---------- Definition prüfen (Veröffentlichung, R03, T05) ---------- */

export interface FormDefIssue {
  where: string;
  message: string;
  /** blockiert die Veröffentlichung */
  blocking: boolean;
}

const OPS_FOR: Record<string, FormOp[]> = {
  choice: ['eq', 'neq', 'in', 'answered', 'unanswered'],
  yesno: ['eq', 'neq', 'answered', 'unanswered'],
  confirm: ['eq', 'answered', 'unanswered'],
  multichoice: ['contains', 'containsAny', 'containsAll', 'answered', 'unanswered'],
  text: ['eq', 'neq', 'contains', 'startsWith', 'answered', 'unanswered'],
  number: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'answered', 'unanswered'],
  date: ['eq', 'gt', 'lt', 'between', 'answered', 'unanswered'],
  file: ['answered', 'unanswered'],
  section: ['answered', 'unanswered', 'gte', 'lte', 'eq'],
};
export function opsForType(t: FormQuestionType | 'section'): FormOp[] {
  if (t === 'longtext' || t === 'email' || t === 'phone' || t === 'url') return OPS_FOR.text!;
  if (t === 'currency' || t === 'rating' || t === 'calc') return OPS_FOR.number!;
  if (t === 'datetime') return OPS_FOR.date!;
  return OPS_FOR[t] ?? ['answered', 'unanswered'];
}

export const FORM_OP_LABELS: Record<FormOp, string> = {
  eq: 'ist',
  neq: 'ist nicht',
  in: 'ist eine von',
  contains: 'enthält',
  containsAny: 'enthält mindestens eine von',
  containsAll: 'enthält alle von',
  startsWith: 'beginnt mit',
  gt: 'ist größer als',
  gte: 'ist mindestens',
  lt: 'ist kleiner als',
  lte: 'ist höchstens',
  between: 'liegt zwischen',
  answered: 'ist beantwortet',
  unanswered: 'ist unbeantwortet',
};

/** Struktur, Verweise (nur auf frühere Fragen), Grenzen. Zuordnung prüft der Server gegen das Board. */
export function checkFormStructure(def: FormDefinition): FormDefIssue[] {
  const issues: FormDefIssue[] = [];
  const bad = (where: string, message: string, blocking = true) => issues.push({ where, message, blocking });
  const flat = flattenQuestions(def);
  if (!def.pages.length) bad('form', 'Das Formular braucht mindestens eine Seite.');
  if (def.pages.length > FORM_LIMITS.pages) bad('form', `Höchstens ${FORM_LIMITS.pages} Seiten.`);
  if (flat.length > FORM_LIMITS.questions) bad('form', `Höchstens ${FORM_LIMITS.questions} Fragen.`);
  const ids = new Set<string>();
  const order = new Map<string, number>(); // Dokumentposition
  const sectionOf = new Map<string, FormSection | null>();
  const typeOf = new Map<string, FormQuestionType | 'section'>();
  let pos = 0;
  for (const p of def.pages) {
    if (ids.has(p.id)) bad(p.id, 'Doppelte ID.');
    ids.add(p.id);
    for (const it of p.items) {
      if (ids.has(it.id)) bad(it.id, 'Doppelte ID.');
      ids.add(it.id);
      if (it.kind === 'section') {
        order.set(it.id, pos);
        typeOf.set(it.id, 'section');
        sectionOf.set(it.id, null);
        for (const q of it.items) {
          if (ids.has(q.id)) bad(q.id, 'Doppelte ID.');
          ids.add(q.id);
          order.set(q.id, ++pos);
          sectionOf.set(q.id, it);
          typeOf.set(q.id, q.type);
        }
        // Abschnittsende: Summen nach dem Abschnitt verfügbar
        pos++;
        order.set(`${it.id}#end`, pos);
      } else {
        order.set(it.id, ++pos);
        sectionOf.set(it.id, null);
        typeOf.set(it.id, it.type);
      }
    }
  }
  const labelOf = (id: string) => flat.find((f) => f.q.id === id)?.q.label ?? findSection(def, id)?.title ?? id;
  let rules = 0;

  /** Verweise einer Bedingung an Stelle `at` (Frage/Seite) im Kontext `sec` */
  const checkCond = (c: FormCondition | null | undefined, at: string, atPos: number, sec: FormSection | null, what: string) => {
    if (!c || !c.items.length) return;
    rules++;
    const atoms = conditionAtoms(c);
    if (atoms.length > FORM_LIMITS.conditionsPerRule) bad(at, `${what}: höchstens ${FORM_LIMITS.conditionsPerRule} Bedingungen je Regel.`);
    if (c.items.some((it) => !('q' in it) && it.items.some((x) => !('q' in x)))) bad(at, `${what}: höchstens zwei Ebenen.`);
    for (const a of atoms) {
      if (a.q === at) {
        bad(at, `„${labelOf(at)}“ bezieht sich auf sich selbst.`);
        continue;
      }
      const t = typeOf.get(a.q);
      if (!t) {
        bad(at, `${what}: Die Frage im Vergleich gibt es nicht mehr.`);
        continue;
      }
      const refSec = sectionOf.get(a.q);
      if (refSec?.repeat && refSec !== sec) {
        bad(at, `${what}: „${labelOf(a.q)}“ liegt in einem wiederholbaren Abschnitt und kann hier nicht verglichen werden.`);
        continue;
      }
      const refPos = t === 'section' ? (order.get(`${a.q}#end`) ?? Infinity) : (order.get(a.q) ?? Infinity);
      if (refPos >= atPos) bad(at, `${what}: „${labelOf(a.q)}“ steht nicht vor „${labelOf(at)}“. Bedingungen dürfen nur frühere Fragen verwenden.`);
      if (!opsForType(t).includes(a.op)) bad(at, `${what}: Vergleich „${FORM_OP_LABELS[a.op]}“ passt nicht zu „${labelOf(a.q)}“.`);
      if (t === 'choice' || t === 'multichoice') {
        const q = flat.find((f) => f.q.id === a.q)!.q;
        const vals = Array.isArray(a.value) ? a.value : a.value != null ? [a.value] : [];
        if (vals.some((v) => !q.options?.some((o) => o.id === v))) bad(at, `${what}: Antwort im Vergleich gibt es bei „${q.label}“ nicht mehr.`);
      }
      if (!['answered', 'unanswered'].includes(a.op) && (a.value === undefined || a.value === null || a.value === '')) bad(at, `${what}: Vergleichswert fehlt.`);
    }
  };

  for (const p of def.pages) {
    const pPos = order.get(p.items[0]?.id ?? '') ?? pos + 1;
    if (!p.title.trim() && def.pages.length > 1) bad(p.id, 'Seite ohne Titel.', false);
    checkCond(p.visibleIf, p.id, pPos - 0.5, null, `Seite „${p.title || 'ohne Titel'}“`);
    if (!p.items.length) bad(p.id, `Seite „${p.title || 'ohne Titel'}“ ist leer.`, false);
    for (const it of p.items) {
      if (it.kind === 'section') {
        checkCond(it.visibleIf, it.id, order.get(it.id)!, null, `Abschnitt „${it.title}“`);
        if (it.repeat && (it.repeat.min < 0 || it.repeat.max < 1 || it.repeat.min > it.repeat.max || it.repeat.max > FORM_LIMITS.rows))
          bad(it.id, `Abschnitt „${it.title}“: Anzahl der Einträge zwischen 0 und ${FORM_LIMITS.rows}.`);
        if (!it.items.length) bad(it.id, `Abschnitt „${it.title}“ ist leer.`);
      }
      const qs = it.kind === 'section' ? it.items : [it];
      for (const q of qs) {
        const sec = it.kind === 'section' ? it : null;
        const qPos = order.get(q.id)!;
        if (!q.label.trim()) bad(q.id, 'Frage ohne Beschriftung.');
        checkCond(q.visibleIf, q.id, qPos, sec, `„${q.label}“ anzeigen`);
        checkCond(q.requiredIf, q.id, qPos, sec, `„${q.label}“ Pflicht`);
        if (q.type === 'choice' || q.type === 'multichoice') {
          const opts = q.options ?? [];
          if (opts.length < 1) bad(q.id, `„${q.label}“ braucht Antwortmöglichkeiten.`);
          if (opts.length > FORM_LIMITS.options) bad(q.id, `„${q.label}“: höchstens ${FORM_LIMITS.options} Antworten.`);
          if (new Set(opts.map((o) => o.id)).size !== opts.length) bad(q.id, `„${q.label}“: doppelte Antwort-ID.`);
          if (opts.some((o) => !o.label.trim())) bad(q.id, `„${q.label}“: Antwort ohne Text.`);
          for (const o of opts) checkCond(o.visibleIf, q.id, qPos, sec, `Antwort „${o.label}“`);
        }
        if (q.type === 'calc') {
          if (!q.formula) bad(q.id, `„${q.label}“ braucht eine Formel.`);
          else {
            const { refs, sums } = exprRefs(q.formula);
            for (const r of refs) {
              const t = typeOf.get(r);
              const rs = sectionOf.get(r);
              if (!t || !['number', 'currency', 'calc', 'rating'].includes(t)) bad(q.id, `Formel „${q.label}“: „${labelOf(r)}“ ist keine Zahl.`);
              else if ((order.get(r) ?? Infinity) >= qPos) bad(q.id, `Formel „${q.label}“: „${labelOf(r)}“ steht nicht davor.`);
              else if (rs?.repeat && rs !== sec) bad(q.id, `Formel „${q.label}“: Werte aus Einträgen nur über SUMME verwenden.`);
            }
            for (const r of sums) {
              const rs = sectionOf.get(r);
              const t = typeOf.get(r);
              if (!rs?.repeat || !t || !['number', 'currency', 'calc'].includes(t)) bad(q.id, `Formel „${q.label}“: SUMME geht nur über Zahlen in wiederholbaren Abschnitten.`);
              else if ((order.get(`${rs.id}#end`) ?? Infinity) >= qPos || rs === sec) bad(q.id, `Formel „${q.label}“: SUMME erst nach dem Abschnitt „${rs.title}“ verwenden.`);
            }
          }
        }
        if (q.notBefore) {
          const t = typeOf.get(q.notBefore);
          if (!t || (t !== 'date' && t !== 'datetime') || (order.get(q.notBefore) ?? Infinity) >= qPos) bad(q.id, `„${q.label}“: Vergleichsdatum muss eine frühere Datumsfrage sein.`);
        }
        if (q.min !== undefined && q.max !== undefined && q.min > q.max) bad(q.id, `„${q.label}“: Minimum größer als Maximum.`);
      }
    }
  }
  for (const r of def.routes) checkCond(r.when, `route:${r.id}`, Infinity, null, `Zuordnungsregel „${r.name}“`);
  if (def.routes.length > FORM_LIMITS.routes) bad('routes', `Höchstens ${FORM_LIMITS.routes} Zuordnungsregeln.`);
  if (rules > FORM_LIMITS.rules) bad('form', `Höchstens ${FORM_LIMITS.rules} Regeln.`);
  // Pflicht-Fragen ohne Weg zum Abschluss gibt es nicht: Seiten werden nur übersprungen, nie gesprungen (R04)
  return issues;
}

function conditionAtoms(c: FormCondition): FormAtom[] {
  return c.items.flatMap((it) => ('q' in it ? [it] : conditionAtoms(it)));
}

/* ---------- Öffentliche Darstellung (A02) ---------- */

export function publicDefinition(def: FormDefinition): FormPublicDefinition {
  return { schemaVersion: def.schemaVersion, title: def.title, intro: def.intro, pages: def.pages, confirmation: { text: def.confirmation.text } };
}

/* ---------- Anzeige von Antworten ---------- */

export function formatAnswer(q: FormQuestion, v: FormValue | undefined, uploads?: Map<string, { name: string }>): string {
  if (isEmpty(v)) return '';
  switch (q.type) {
    case 'choice':
      return q.options?.find((o) => o.id === v)?.label ?? '';
    case 'multichoice':
      return (v as string[]).map((id) => q.options?.find((o) => o.id === id)?.label ?? '').filter(Boolean).join(', ');
    case 'yesno':
      return v ? 'Ja' : 'Nein';
    case 'confirm':
      return v ? 'Bestätigt' : 'Nicht bestätigt';
    case 'number':
    case 'calc':
      return `${(v as number).toLocaleString('de-DE', { minimumFractionDigits: q.type === 'calc' ? (q.decimals ?? 2) : 0, maximumFractionDigits: q.decimals ?? 10 })}${q.unit ? ' ' + q.unit : ''}`;
    case 'currency':
      return (v as number).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
    case 'rating':
      return `${v} von 5`;
    case 'date': {
      const [y, m, d] = String(v).split('-');
      return `${d}.${m}.${y}`;
    }
    case 'datetime': {
      const t = new Date(String(v));
      return Number.isNaN(t.getTime())
        ? ''
        : t.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin', timeZoneName: 'short' });
    }
    case 'file':
      return (v as string[]).map((id) => uploads?.get(id)?.name ?? 'Datei').join(', ');
    default:
      return String(v);
  }
}

/* ---------- Kartenwerte (Z01, Z02) ---------- */

export type FormValueOrigin = 'Vorlage' | 'Formular' | `Antwort „${string}“` | `Regel „${string}“`;

export interface FormCardResult {
  columnId: string | null;
  title: string;
  description: string | null;
  labelIds: string[];
  priority: number;
  assigneeIds: string[];
  startAt: { iso: string; hasTime: boolean } | null;
  dueAt: { iso: string; hasTime: boolean } | null;
  dueInDays: number | null;
  custom: { fieldId: string; q: string; value: FormValue }[];
  checklist: string[];
  /** Upload-IDs in Anzeigereihenfolge */
  files: string[];
  coverUpload: string | null;
  routeId: string | null;
  routeName: string | null;
  origins: Record<string, FormValueOrigin>;
  errors: string[];
}

const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const textToHtml = (s: string) =>
  s
    .split(/\n{2,}/)
    .map((p) => `<p>${escapeHtml(p).replace(/\n/g, '<br>')}</p>`)
    .join('');

/** Kartenbeschreibung aus Antworten: „Frage: Antwort“, lange Texte als Absatz, Einträge als Liste */
export function answersToHtml(def: Pick<FormDefinition, 'pages'>, ev: FormEvaluation, onlyIds?: string[] | null, uploads?: Map<string, { name: string }>): string {
  const parts: string[] = [];
  const want = (id: string) => !onlyIds || onlyIds.includes(id);
  for (const page of def.pages) {
    if (!ev.pages.includes(page.id)) continue;
    for (const item of page.items) {
      if (item.kind === 'question') {
        if (!want(item.id) || !isAnswerable(item) || !ev.visible.has(item.id)) continue;
        if (item.type === 'calc' || item.type === 'info') continue;
        const v = ev.active[item.id];
        if (isEmpty(v)) continue;
        if (item.type === 'longtext') parts.push(`<p><strong>${escapeHtml(item.label)}:</strong></p>${textToHtml(String(v))}`);
        else parts.push(`<p><strong>${escapeHtml(item.label)}:</strong> ${escapeHtml(formatAnswer(item, v, uploads))}</p>`);
        continue;
      }
      if (!ev.visible.has(item.id)) continue;
      if (item.repeat) {
        if (!want(item.id)) continue;
        const rows = rowsOf(ev.active[item.id]);
        if (!rows.length) continue;
        const li = rows.map((r, i) => {
          const cells = item.items
            .filter((q) => q.type !== 'info' && !isEmpty(r[q.id]))
            .map((q) => `${escapeHtml(q.label)}: ${escapeHtml(formatAnswer(q, r[q.id], uploads))}`);
          return `<li><p>${escapeHtml(item.repeat!.itemLabel || 'Eintrag')} ${i + 1}: ${cells.join(' · ')}</p></li>`;
        });
        parts.push(`<p><strong>${escapeHtml(item.title)}</strong></p><ul>${li.join('')}</ul>`);
        continue;
      }
      for (const q of item.items) {
        if (!want(q.id) || !ev.visible.has(q.id) || q.type === 'info') continue;
        if (q.type === 'calc') continue; // Berechnungen stehen gesammelt am Ende
        const v = ev.active[q.id];
        if (isEmpty(v)) continue;
        parts.push(`<p><strong>${escapeHtml(q.label)}:</strong> ${escapeHtml(formatAnswer(q, v as FormValue, uploads))}</p>`);
      }
    }
  }
  // Berechnete Werte (außerhalb wiederholter Einträge) am Ende, z. B. Gesamtsummen
  for (const f of flattenQuestions(def)) {
    if (f.q.type !== 'calc' || f.section?.repeat || !want(f.q.id) || !ev.visible.has(f.q.id)) continue;
    const c = ev.calc[f.q.id];
    if (c?.value != null) parts.push(`<p><strong>${escapeHtml(f.q.label)}:</strong> ${escapeHtml(formatAnswer(f.q, c.value))}</p>`);
  }
  return parts.join('');
}

/** Platzhalter {{q:<id>}} durch Antworten ersetzen */
export function renderTitle(template: string, def: Pick<FormDefinition, 'pages'>, ev: FormEvaluation): string {
  const flat = new Map(flattenQuestions(def).map((f) => [f.q.id, f.q]));
  return template
    .replace(/\{\{q:([\w-]+)\}\}/g, (_m, id: string) => {
      const q = flat.get(id);
      if (!q) return '';
      const v = q.type === 'calc' ? (ev.calc[id]?.value ?? null) : ev.active[id];
      return formatAnswer(q, v).replace(/\s+/g, ' ');
    })
    .replace(/\s+/g, ' ')
    .replace(/^[\s·\-–:,]+|[\s·\-–:,]+$/g, '')
    .trim();
}

export interface CardTemplateSnapshot {
  description?: string | null;
  labelIds?: string[];
  checklist?: string[];
  priority?: number;
  dueInDays?: number | null;
}

/**
 * Endgültige Kartenwerte: Vorlagenschnappschuss → Formularvorgaben → Antworten → erste passende Regel (Z02).
 * Verweise (Spalten, Labels, Personen) prüft der Server danach gegen das Board.
 */
export function resolveFormCard(def: FormDefinition, ev: FormEvaluation, opts: { snapshot?: CardTemplateSnapshot | null; uploads?: FormUploadInfo[] } = {}): FormCardResult {
  const m = def.mapping;
  const flat = new Map(flattenQuestions(def).map((f) => [f.q.id, f]));
  const label = (id: string) => flat.get(id)?.q.label ?? '?';
  const origins: Record<string, FormValueOrigin> = {};
  const errors: string[] = [];
  const snap = opts.snapshot ?? null;
  const uploadMap = new Map((opts.uploads ?? []).map((u) => [u.id, u]));
  const look: Lookup = (q) => {
    const f = flat.get(q);
    if (f) return { value: f.q.type === 'calc' ? (ev.calc[q]?.value ?? null) : ev.active[q], type: f.q.type };
    const s = findSection(def, q);
    return s ? { value: ev.active[q], type: 'section' } : null;
  };

  // Vorlage
  let labelIds = [...(snap?.labelIds ?? [])];
  let priority = snap?.priority ?? 0;
  let dueInDays: number | null = snap?.dueInDays ?? null;
  const checklist = [...(snap?.checklist ?? [])];
  if (snap?.labelIds?.length) origins.labels = 'Vorlage';
  if (snap?.priority) origins.priority = 'Vorlage';
  if (snap?.dueInDays != null) origins.due = 'Vorlage';
  if (checklist.length) origins.checklist = 'Vorlage';

  // Formularvorgaben
  let columnId = m.columnId;
  origins.column = 'Formular';
  if (m.labelIds.length) {
    labelIds = [...new Set([...labelIds, ...m.labelIds])];
    origins.labels = 'Formular';
  }
  if (m.priority) {
    priority = m.priority;
    origins.priority = 'Formular';
  }
  let assigneeIds = [...m.assigneeIds];
  if (assigneeIds.length) origins.assignees = 'Formular';

  // Antworten
  const title = renderTitle(m.title, def, ev);
  origins.title = 'Formular';
  if (!title) errors.push('Der Kartentitel wäre leer. Bitte die Fragen im Titel beantworten oder den Titel anpassen.');
  if (title.length > 500) errors.push('Der Kartentitel wäre zu lang (über 500 Zeichen).');
  let description: string | null = null;
  if (m.description.mode === 'summary') {
    const html = answersToHtml(def, ev, m.description.questionIds, uploadMap);
    description = [snap?.description ?? '', html].filter(Boolean).join('') || null;
    origins.description = 'Formular';
  } else if (snap?.description) {
    description = snap.description;
    origins.description = 'Vorlage';
  }
  const custom: FormCardResult['custom'] = [];
  for (const f of m.fields) {
    const hit = look(f.q);
    if (!hit || isEmpty(hit.value)) continue;
    custom.push({ fieldId: f.fieldId, q: f.q, value: hit.value! });
    origins[`field:${f.fieldId}`] = `Antwort „${label(f.q)}“`;
  }
  const dateOf = (q: string): { iso: string; hasTime: boolean } | null => {
    const f = flat.get(q);
    const v = ev.active[q];
    if (!f || typeof v !== 'string' || !v) return null;
    if (f.q.type === 'date') return { iso: `${v}T00:00:00.000Z`, hasTime: false };
    const t = Date.parse(v);
    return Number.isNaN(t) ? null : { iso: new Date(t).toISOString(), hasTime: true };
  };
  let startAt = m.start ? dateOf(m.start.q) : null;
  if (startAt) origins.start = `Antwort „${label(m.start!.q)}“`;
  let dueAt: FormCardResult['dueAt'] = null;
  if (m.due && 'q' in m.due) {
    dueAt = dateOf(m.due.q);
    if (dueAt) {
      origins.due = `Antwort „${label(m.due.q)}“`;
      dueInDays = null;
    }
  } else if (m.due && 'days' in m.due) {
    dueInDays = m.due.days;
    origins.due = 'Formular';
  }
  if (startAt && dueAt && Date.parse(startAt.iso) > Date.parse(dueAt.iso)) {
    startAt = null;
    errors.push('Der Beginn läge nach der Frist.');
  }
  const files: string[] = [];
  let coverUpload: string | null = null;
  for (const f of flattenQuestions(def)) {
    if (f.q.type !== 'file') continue;
    const take = (v: FormValue | undefined) => {
      for (const id of Array.isArray(v) ? (v as string[]) : []) if (!files.includes(id)) files.push(id);
    };
    if (f.section?.repeat) for (const r of rowsOf(ev.active[f.section.id])) take(r[f.q.id]);
    else take(ev.active[f.q.id]);
  }
  if (m.coverQ) {
    const ids = Array.isArray(ev.active[m.coverQ]) ? (ev.active[m.coverQ] as string[]) : [];
    coverUpload = ids.find((id) => uploadMap.get(id)?.mime.startsWith('image/')) ?? null;
    if (coverUpload) origins.cover = `Antwort „${label(m.coverQ)}“`;
  }

  // Genau eine Regel: die erste passende (Z02)
  let routeId: string | null = null;
  let routeName: string | null = null;
  for (const r of def.routes) {
    if (!evalCondition(r.when, look)) continue;
    routeId = r.id;
    routeName = r.name;
    const o: FormValueOrigin = `Regel „${r.name}“`;
    if (r.columnId) {
      columnId = r.columnId;
      origins.column = o;
    }
    if (r.labels) {
      labelIds = r.labels.mode === 'replace' ? [...r.labels.ids] : [...new Set([...labelIds, ...r.labels.ids])];
      origins.labels = o;
    }
    if (r.assignees) {
      assigneeIds = r.assignees.mode === 'replace' ? [...r.assignees.ids] : [...new Set([...assigneeIds, ...r.assignees.ids])];
      origins.assignees = o;
    }
    if (r.priority != null) {
      priority = r.priority;
      origins.priority = o;
    }
    if (r.dueDays != null && !dueAt) {
      dueInDays = r.dueDays;
      origins.due = o;
    }
    break;
  }
  if (!columnId) errors.push('Es ist keine Zielspalte festgelegt.');
  return { columnId, title, description, labelIds, priority, assigneeIds, startAt, dueAt, dueInDays, custom, checklist, files, coverUpload, routeId, routeName, origins, errors };
}

/* ---------- Typverträglichkeit Frage → Boardfeld (Z01) ---------- */

export const FORM_FIELD_COMPAT: Record<FormQuestionType, string[]> = {
  text: ['text', 'longtext'],
  longtext: ['longtext', 'text'],
  email: ['email', 'text'],
  phone: ['phone', 'text'],
  url: ['url', 'text'],
  number: ['number'],
  currency: ['currency', 'number'],
  choice: ['select', 'text'],
  multichoice: ['multiselect'],
  yesno: ['checkbox'],
  confirm: ['checkbox'],
  date: ['date'],
  datetime: ['date'],
  file: [],
  rating: ['rating', 'number'],
  calc: ['number', 'currency'],
  info: [],
};

/** Neue IDs im Browser ohne crypto.randomUUID (siehe CLAUDE.md) */
export function formId(prefix = 'q'): string {
  return `${prefix}${Math.random().toString(36).slice(2, 8)}${Date.now().toString(36).slice(-3)}`;
}

/** Einsendung: angezeigte Eingangsnummer */
export const submissionNumber = (seq: number) => `E-${String(seq).padStart(4, '0')}`;


/* ---------- API-Formen ---------- */

export type FormStatusName = 'draft' | 'active' | 'paused' | 'archived';

export interface FormSummary {
  id: string;
  boardId: string;
  title: string;
  status: FormStatusName;
  pauseReason: string | null;
  activeVersion: number | null;
  hasDraftChanges: boolean;
  submissionCount: number;
  repairIssues: string[];
  fillPath: string;
  updatedAt: string;
}

export interface FormDetail extends FormSummary {
  draft: FormDefinition;
  draftRevision: number;
  access: { allTeam: boolean; groupIds: string[]; userIds: string[] };
  opensAt: string | null;
  closesAt: string | null;
  closedMessage: string | null;
  maxSubmissions: number | null;
  dailyLimit: number | null;
  notify: { userIds: string[]; groupIds: string[] };
  /** Öffentlicher Formularlink (ohne Anmeldung) */
  publicEnabled: boolean;
  publicUrl: string | null;
  /** Virenprüfung eingerichtet (Voraussetzung für öffentliche Dateifragen) */
  scanner: boolean;
  mailEnabled: boolean;
  versions: { id: string; number: number; publishedAt: string; publishedBy: string | null; withdrawnAt: string | null }[];
  board: {
    id: string;
    title: string;
    teamId: string | null;
    columns: { id: string; title: string }[];
    labels: { id: string; name: string; color: import('./types.js').LabelColor }[];
    fields: { id: string; name: string; type: import('./types.js').FieldType; options: import('./types.js').FieldOption[] }[];
    assignable: { id: string; name: string }[];
    groups: { id: string; name: string }[];
    people: { id: string; name: string }[];
    cardTemplates: { id: string; name: string }[];
  };
}

export interface FormFillState {
  form: { id: string; title: string; boardTitle: string };
  /** Grund, warum gerade nichts eingesendet werden kann */
  closed: string | null;
  /** Die begonnene Fassung wurde zurückgezogen – kompatible Antworten wurden übernommen */
  withdrawn: boolean;
  newerVersion: boolean;
  definition: FormPublicDefinition | null;
  session: { id: string; revision: number; answers: FormAnswers; expiresAt: string; uploads: FormUploadInfo[] } | null;
  /** Boardlogo und -farbe (A04) */
  brand?: import('./branding.js').EntryBrand | null;
}

export interface FormSubmitResult {
  number: string;
  cardId: string;
  boardId: string;
  confirmation: string;
  expectation: string | null;
  canOpenCard: boolean;
}

export interface FormSubmissionView {
  number: string;
  formTitle: string;
  version: number;
  at: string;
  submitter: string | null;
  contact: string | null;
  route: string | null;
  definition: FormPublicDefinition;
  answers: FormAnswers;
  files: { id: string; name: string }[];
}

export interface FormSimulation {
  pages: string[];
  visible: string[];
  required: string[];
  removed: string[];
  calc: Record<string, { value: number | null; error?: 'missing' | 'div0' }>;
  issues: FormIssue[];
  card: {
    column: string | null;
    title: string;
    description: string | null;
    labels: string[];
    priority: number;
    assignees: string[];
    start: string | null;
    due: string | null;
    fields: { name: string; value: string }[];
    route: string | null;
    origins: Record<string, string>;
    errors: string[];
    hints: string[];
  };
}

/* ---------- Hilfen für Oberfläche und Server ---------- */

/** Aktive Antworten ohne berechnete Werte – so werden Entwürfe gespeichert (R03) */
export function cleanFormAnswers(def: Pick<FormDefinition, 'pages'>, ev: FormEvaluation): FormAnswers {
  const calc = new Set(flattenQuestions(def).filter((f) => f.q.type === 'calc').map((f) => f.q.id));
  const out: FormAnswers = {};
  for (const [k, v] of Object.entries(ev.active)) {
    if (calc.has(k)) continue;
    if (Array.isArray(v) && v.length && typeof v[0] === 'object' && v[0] !== null) {
      out[k] = (v as FormRowValue[]).map((r) => {
        const row: FormRowValue = { _id: r._id };
        for (const [rk, rv] of Object.entries(r)) if (rk !== '_id' && !calc.has(rk)) row[rk] = rv;
        return row;
      });
    } else out[k] = v;
  }
  return out;
}

/** Bedingung als Satz: „Leistung ist Druckprodukt und Auflage ist mindestens 100“ */
export function describeFormCondition(def: Pick<FormDefinition, 'pages'>, c: FormCondition | null | undefined): string {
  if (!c || !c.items.length) return '';
  const flat = new Map(flattenQuestions(def).map((f) => [f.q.id, f.q]));
  const atom = (a: FormAtom): string => {
    const q = flat.get(a.q);
    const sec = q ? null : findSection(def, a.q);
    const name = q?.label ?? (sec ? `Anzahl der Einträge in „${sec.title}“` : '?');
    if (a.op === 'answered' || a.op === 'unanswered') return `„${name}“ ${FORM_OP_LABELS[a.op]}`;
    const opText = formOpLabel(a.op, q?.type ?? (sec ? 'section' : undefined));
    const optLabel = (v: unknown) => q?.options?.find((o) => o.id === v)?.label ?? String(v);
    let val: string;
    if (q?.type === 'yesno' || q?.type === 'confirm') val = a.value ? 'Ja' : 'Nein';
    else if (q && (q.type === 'choice' || q.type === 'multichoice')) val = (Array.isArray(a.value) ? a.value : [a.value]).map((v) => `„${optLabel(v)}“`).join(', ');
    else if (a.op === 'between' && Array.isArray(a.value)) val = `${fmtVal(q, a.value[0])} und ${fmtVal(q, a.value[1])}`;
    else val = q?.type === 'text' || q?.type === 'longtext' ? `„${String(a.value ?? '')}“` : fmtVal(q, a.value);
    return `„${name}“ ${opText} ${val}`;
  };
  const group = (g: FormGroup, top: boolean): string => {
    const parts = g.items.map((it) => ('q' in it ? atom(it) : `(${group(it, false)})`));
    const s = parts.join(g.mode === 'all' ? ' und ' : ' oder ');
    return top || parts.length < 2 ? s : s;
  };
  return group(c, true);
}

function fmtVal(q: FormQuestion | undefined, v: unknown): string {
  if (typeof v === 'number') return v.toLocaleString('de-DE');
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v.split('-').reverse().join('.');
  void q;
  return String(v ?? '');
}

/** Vergleich passend zum Typ benennen (Datum: „liegt vor/nach“) */
export function formOpLabel(op: FormOp, type: FormQuestionType | 'section' | undefined): string {
  if (type === 'date' || type === 'datetime') {
    if (op === 'gt') return 'liegt nach';
    if (op === 'lt') return 'liegt vor';
    if (op === 'eq') return 'ist am';
  }
  return FORM_OP_LABELS[op];
}

/** Öffentliches Ausfüllen (ohne Anmeldung): keine Board- oder Sitzungs-IDs */
export interface FormPublicFillState {
  title: string;
  closed: string | null;
  definition: FormPublicDefinition | null;
  session: { revision: number; answers: FormAnswers; expiresAt: string; uploads: FormUploadInfo[] } | null;
  /** Bestätigung und Fortsetzen per E-Mail möglich */
  mailEnabled: boolean;
  /** Entwurf eben von einem anderen Gerät übernommen */
  resumed: boolean;
  /** Boardlogo und -farbe (A04) */
  brand?: import('./branding.js').EntryBrand | null;
}

/* ---------- Auswahloptionen ↔ Boardfeldoptionen (F03) ---------- */

const optNorm = (s: string) => s.trim().toLocaleLowerCase('de-DE');

/** Einrichthilfe: eindeutige Namensgleichheit vorschlagen (mehrdeutige oder fehlende Treffer bleiben offen) */
export function suggestOptionMap(formOptions: { id: string; label: string }[], fieldOptions: { id: string; label: string }[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const o of formOptions) {
    const hits = fieldOptions.filter((x) => optNorm(x.label) === optNorm(o.label));
    if (hits.length === 1) out[o.id] = hits[0]!.id;
  }
  return out;
}

/**
 * Verbindliche Zuordnung einer Auswahlfrage: gespeicherte IDs, sonst (alte Entwürfe) der Vorschlag.
 * `missing`: Formularoptionen ohne gültiges Ziel (gelöscht, nicht zugeordnet oder mehrdeutig).
 */
export function resolveOptionMap(
  formOptions: { id: string; label: string }[],
  fieldOptions: { id: string; label: string }[],
  explicit: Record<string, string> | null | undefined,
): { map: Map<string, string>; missing: { id: string; label: string }[] } {
  const base = explicit ?? suggestOptionMap(formOptions, fieldOptions);
  const valid = new Set(fieldOptions.map((x) => x.id));
  const map = new Map<string, string>();
  const missing: { id: string; label: string }[] = [];
  for (const o of formOptions) {
    const t = base[o.id];
    if (t && valid.has(t)) map.set(o.id, t);
    else missing.push(o);
  }
  return { map, missing };
}
